import { Suspense, useState } from "react";
import { Button, Col, Form, Spinner, Stack, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { axiosInstance } from "../../auth/Auth";
import ErrorResponse from "../ErrorResponse";
import { Await, useLoaderData } from "react-router-dom";
import StationPicker from "../Pickers/StationPicker";
import { useUserFormState } from "../UserSelection";

const extraDefaults = { timeFrame: 0 };

export default function PlaylistAutopilotImportUpload({ setData, setInputData }) {
    const moduleName = "playlistAutopilotImport";
    const { customPlaylistAutopilotRadios } = useLoaderData();

    const [formData, setFormData] = useUserFormState(moduleName, extraDefaults);

    const [errorCode, setErrorCode] = useState(null);
    const [loading, setLoading] = useState(false);

    const { acceptedFiles, getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
        accept: { "text/csv": [".csv"] },
        multiple: false,
        disabled: loading,
    });

    async function uploadFile(event) {
        event.preventDefault();
        try {
            setLoading(true);
            const input = new FormData();
            input.append("file", acceptedFiles[0]);
            const response = await axiosInstance.post(`/playlist_autopilot/custom/${formData.station[0].radio_id}`, input, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setData(response.data);
            setInputData({ radioId: formData.station[0].radio_id, timeFrame: formData.timeFrame });
            setErrorCode(null);
        } catch (error) {
            setData(null);
            setErrorCode(error?.response?.status ?? 500);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Stack direction="horizontal" gap={5} className="d-flex flex-column flex-lg-row align-items-stretch">
                <Col>
                    <Form onSubmit={uploadFile}>
                        <Stack gap={4} className=" align-items-center">
                            <Suspense>
                                <Await resolve={customPlaylistAutopilotRadios}>
                                    {resolvedPlapRadios => (
                                        <StationPicker
                                            formData={formData}
                                            setFormData={setFormData}
                                            name={moduleName}
                                            colXl={12}
                                            disabled={loading}
                                            filterFunction={x => resolvedPlapRadios.data.includes(x.radio_id)}
                                        />
                                    )}
                                </Await>
                            </Suspense>
                            <Form.Group as={Col} className="d-flex flex-column col-12">
                                <Form.Label>Time Frame</Form.Label>
                                <ToggleButtonGroup
                                    type={"radio"}
                                    name="dataType"
                                    value={formData.timeFrame}
                                    onChange={value => setFormData({ ...formData, timeFrame: value })}
                                    className="ra-switcher">
                                    <ToggleButton id={`time-frame-0`} variant={"ra-dark-blue-outline"} value={0}>
                                        Last Week
                                    </ToggleButton>
                                    <ToggleButton id={`time-frame-1`} variant={"ra-dark-blue-outline"} value={1}>
                                        Last Month
                                    </ToggleButton>
                                    <ToggleButton id={`time-frame-2`} variant={"ra-dark-blue-outline"} value={2}>
                                        Last Quarter
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Form.Group>
                            <Stack
                                {...getRootProps({
                                    className: `dropzone ${isDragAccept ? "border-success" : ""} ${isDragReject ? "border-danger" : ""}`,
                                })}
                                gap={2}>
                                <input {...getInputProps()} />
                                <span className="fw-bold">Drag 'n' drop to upload {!!acceptedFiles?.length ? "different" : "a"} file</span>
                                <span>OR</span>
                                <Button variant="ra-dark-blue" disabled={loading}>
                                    Choose {!!acceptedFiles?.length && "Different"} File
                                </Button>
                                <span className="fw-bold">{acceptedFiles[0]?.path}</span>
                            </Stack>
                            <Button
                                type="submit"
                                variant="ra-dark-blue"
                                className="px-5"
                                disabled={!acceptedFiles?.length || !formData.station.length || loading}>
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Processing...
                                    </>
                                ) : (
                                    <>Upload & Continue</>
                                )}
                            </Button>
                        </Stack>
                    </Form>
                </Col>
                <div className="vr opacity-100 d-none d-lg-block"></div>
                <hr className="d-block opacity-100 d-lg-none" />
                <Col>
                    <h5>File Requirements:</h5>
                    <h6>General:</h6>
                    <ul className="mb-3">
                        <li>CSV file (.csv)</li>
                        <li>Semicolon (;) as separator</li>
                        <li>No headers</li>
                    </ul>
                    <h6>Columns:</h6>
                    <ul className="mb-3">
                        <li>Song ID (from Music planning system)</li>
                        <li>Item ID (RadioAnalyzer’s ID)</li>
                        <li>Artist</li>
                        <li>Title</li>
                        <li>Release year</li>
                        <li>Category (including level)</li>
                    </ul>
                </Col>
            </Stack>
            {errorCode && (
                <Col xs={12} className="mt-3">
                    <ErrorResponse errorCode={errorCode} />
                </Col>
            )}
        </>
    );
}
